import React, { useContext, useEffect } from "react"

import { NotificationManager } from "react-notifications"
import { RouteComponentProps } from "react-router"
import { useHistory } from "react-router-dom"
import { gql, useQuery } from "urql"

import { CheckIcon } from "@heroicons/react/24/solid"

import { AlertLevel, Color } from "~tailwindui/types/enums"

import NotFound from "~Tutor/Pages/NotFound"
import TutorContext from "~Tutor/TutorContext"
import { path } from "~Tutor/TutorRoutes"
import {
  AlertMessageBox,
  H3,
  StyledLink,
  WithLoadingIndicator,
} from "~tailwindui/Basics"
import DescriptionItem from "~tailwindui/Basics/DescriptionItem"
import H4 from "~tailwindui/Basics/H4"
import Panel from "~tailwindui/Basics/Panel"

import dayjs from "src/dayjs"
import ordered from "src/ordered"
import titlecase from "src/titlecase"
import { MatchParams } from "src/types/MatchParams"

import LocalTime from "components/LocalTime"

import LessonResources from "../components/LessonResources"
import SessionInfo from "../components/SessionInfo"

import SessionResources from "./Resources"
import SessionButtons from "./SessionButtons"
import StudentPresenceStatus from "./StudentPresenceStatus"

const SessionDetails: React.FC<RouteComponentProps<MatchParams>> = ({
  match,
}) => {
  const sessionId = match.params.id
  const context = useContext(TutorContext)
  const tutorId = context.viewer.id
  const history = useHistory()

  const [result, refetchQuery] = useQuery({
    query,
    variables: { sessionId, tutorId },
  })

  useEffect(() => {
    if (result.data && result.data.session?.blocked) {
      history.push(path("dashboard"))
      NotificationManager.warning(
        "This session is blocked. Please wait for the family to resolve their failed payments."
      )
    }
  }, [result, history])

  if (result.data && !result.data.airtutorsSession) return <NotFound />

  return (
    <WithLoadingIndicator result={result}>
      {({ data: { airtutorsSession: session } }) => (
        <>
          {session.statusState === "canceled" && (
            <AlertMessageBox level={AlertLevel.Warning}>
              This session has been canceled due to {session.cancellationReason}
            </AlertMessageBox>
          )}

          {session.future && session.noShow && (
            <AlertMessageBox level={AlertLevel.Warning}>
              This session was auto-billed as a no show due to student
              cancellation(s). You will not need to attend this session.
            </AlertMessageBox>
          )}

          <H3 className="flex items-center">Session Information</H3>
          <hr className="mb-4" />

          <div className="flex flex-col gap-4 md:flex-row">
            <Panel className="grow md:w-1/2">
              <SessionInfo session={session} />
            </Panel>

            <Panel className="md:max-w-md md:grow">
              <H4 className="mb-2">Students</H4>
              <ul className="space-y-2">
                {session.studentSessions.map(({ student }) => (
                  <li key={student.id}>
                    <StyledLink
                      to={path("studentDetails", {
                        id: student.id,
                      })}
                    >
                      {student.fullName}
                    </StyledLink>
                    {student.notes && (
                      <span className="italic"> - {student.notes}</span>
                    )}
                  </li>
                ))}
              </ul>
            </Panel>

            {(dayjs(session.endsAt).isAfter(dayjs()) ||
              session.billState !== "billed") && (
              <Panel className="min-w-max">
                <H4 className="text-center">Actions</H4>
                <SessionButtons session={session} />
              </Panel>
            )}
          </div>

          {session.originalTutor && (
            <Panel>
              <div className="mb-2 font-medium">
                Notes from {session.originalTutor.fullName} (original tutor):
              </div>
              <p className="italic text-red-700">{session.substituteNotes}</p>
            </Panel>
          )}

          {(session.manuallyEnded ||
            dayjs().isAfter(dayjs(session.endsAt))) && (
            <>
              <H3 className="flex items-center">Session Summary</H3>
              <hr className="mb-4" />

              <div className="flex flex-col gap-4 sm:flex-row">
                <Panel className="grow">
                  <H4 className="mb-2">Billing</H4>
                  <div className="flex flex-col gap-4 lg:flex-row">
                    <div>
                      <DescriptionItem
                        label="Status"
                        value={titlecase(session.billState)}
                      />
                      <DescriptionItem
                        label="Duration"
                        value={session.formattedBilledDuration}
                      />
                    </div>
                    {session.billState === "unbilled" ? (
                      <StyledLink
                        to={path("sessionBill", { id: session.id })}
                        button
                        color={Color.Teal}
                      >
                        Complete
                      </StyledLink>
                    ) : (
                      <StyledLink
                        to={path("sessionDurationChange", {
                          id: session.id,
                        })}
                        button
                        color={Color.Orange}
                        className="max-h-[min-content]"
                      >
                        Request Duration Update
                      </StyledLink>
                    )}
                  </div>
                </Panel>

                {session.cloudroomRecordings?.length > 0 && (
                  <Panel>
                    <DescriptionItem
                      label="Cloudroom Recordings"
                      value={
                        <ul>
                          {session.cloudroomRecordings.map(
                            ({ url, name, createdAt, duration }) => (
                              <li key={url}>
                                <StyledLink to={url}>
                                  {name} (<LocalTime timestamp={createdAt} />
                                  {duration && `, Duration: ${duration}`})
                                </StyledLink>
                              </li>
                            )
                          )}
                        </ul>
                      }
                    />
                  </Panel>
                )}
              </div>
            </>
          )}

          {["pending", "billed"].includes(session.billState) && (
            <>
              <Panel>
                <H4>Attendance</H4>

                {session.lessonResourceCompletions.length > 0 && (
                  <div className="mb-4">
                    <div>
                      Lesson Resources Completed:
                      <ul className="my-2">
                        {[
                          ...new Set<string>(
                            session.lessonResourceCompletions.map(
                              (completion: {
                                lessonResource: { name: string }
                              }) => completion.lessonResource.name
                            )
                          ),
                        ].map(name => (
                          <li key={name} className="ml-4 flex space-x-2">
                            <CheckIcon className="h-5 w-5 text-green-600" />
                            <span>{name}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <StyledLink
                      to={path("sessionEvaluationEdit", {
                        id: session.id,
                      })}
                      button
                      color={Color.Blue}
                    >
                      Update Student Evaluation
                    </StyledLink>
                  </div>
                )}

                <ul className="flex grow flex-wrap gap-8">
                  {ordered(
                    session.studentSessions.map(studentSession => (
                      <div
                        key={studentSession.id}
                        className="rounded-lg border border-solid p-4"
                      >
                        <StyledLink
                          to={path("studentDetails", {
                            id: studentSession.student.id,
                          })}
                        >
                          {studentSession.student.fullName}
                        </StyledLink>
                        <StudentPresenceStatus
                          studentSessionId={studentSession.id}
                          noShow={studentSession.noShow}
                          excusedAbsence={studentSession.excusedAbsence}
                          participationLevel={studentSession.participationLevel}
                          refetchQuery={refetchQuery}
                        />
                      </div>
                    )),
                    "student",
                    "fullName"
                  )}
                </ul>
              </Panel>

              <Panel>
                <div className="flex flex-col gap-8 sm:flex-row">
                  <div className="flex-1">
                    <div className="mb-1 font-medium">Your Feedback</div>
                    <div className="text-gray-700">{session.tutorFeedback}</div>
                  </div>
                  <div className="flex-1">
                    <div className="mb-1 font-medium">Topics Covered</div>
                    <div className="text-gray-700">{session.topicsCovered}</div>
                  </div>
                </div>
                <hr className="my-4" />
                <div className="mb-1 font-medium">Reported Tech Issues</div>
                <ul className="text-gray-700">
                  {session.sessionTechIssues.length === 0 && (
                    <span className="italic">No issues</span>
                  )}
                  {session.sessionTechIssues.map(issue => (
                    <li key={issue.id}>
                      {issue.issueType}
                      {issue.description && `: ${issue.description}`}
                    </li>
                  ))}
                </ul>
              </Panel>
            </>
          )}

          <H3 className="flex items-center">Session Resources</H3>
          <hr className="mb-4" />

          <Panel className="flex flex-col justify-start gap-8 sm:flex-row">
            {session.attachments?.length > 0 && (
              <div>
                <H4 className="mb-2">Attachments</H4>
                <ul>
                  {session.attachments.map(({ filename, downloadUrl }) => (
                    <li key={downloadUrl}>
                      <StyledLink to={downloadUrl}>{filename}</StyledLink>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div>
              <H4 className="mb-2">Links</H4>
              <SessionResources id={session.id} />
            </div>
          </Panel>

          {session.lessonResources.length > 0 && (
            <Panel className="mb-6">
              <LessonResources airtutorsSessionId={session.id} />
            </Panel>
          )}
        </>
      )}
    </WithLoadingIndicator>
  )
}

const query = gql`
  query SessionDetails($sessionId: ID!, $tutorId: ID!) {
    airtutorsSession(id: $sessionId) {
      id
      startsAt
      endsAt
      substituteNotes
      tutorFeedback
      topicsCovered
      cloudroomActive
      manuallyEnded
      statusState
      billState
      formattedBilledDuration
      canceledBy
      cancellationReason
      immovable
      future
      mayCancelByTutor
      blocked
      mayRequestSubstituteByTutor
      subRequestsLeft(tutorId: $tutorId)
      sessionTechIssues {
        id
        issueType
        description
      }
      studentSessions {
        id
        noShow
        excusedAbsence
        participationLevel
        student {
          id
          fullName
          lastName
          notes
        }
      }
      subject {
        id
        name
      }
      originalTutor {
        id
        fullName
      }
      studyGroup {
        id
        name
        details
      }
      organization {
        id
        name
        details
        emailTimeZone
      }
      cloudroomRecordings {
        url
        name
        createdAt
        duration
      }
      attachments {
        filename
        downloadUrl
      }
      lessonResources {
        id
      }
      lessonResourceCompletions {
        id
        lessonResource {
          id
          name
        }
      }
    }
  }
`

export default SessionDetails
